"use client";

import { useCallback } from "react";
import type { SessionCollab } from "./context";
import {
  CHAT_PAGE_LIMIT,
  mergeCollabMap,
  toSessionCollab,
} from "./session-data-provider-utils";
import type {
  SessionChatActionDependencies,
  SessionChatActions,
} from "./use-session-chat-actions.types";

enum OlderMessagesRequestKind {
  Skip = "skip",
  Exhausted = "exhausted",
  Ready = "ready",
}

type OlderMessagesRequest =
  | { kind: OlderMessagesRequestKind.Skip }
  | { kind: OlderMessagesRequestKind.Exhausted }
  | { kind: OlderMessagesRequestKind.Ready; cursor: string };

type SessionMainChatActions = Pick<
  SessionChatActions,
  "loadOlderMessages" | "refreshChat"
>;

type UseSessionMainChatActionsInput = Pick<
  SessionChatActionDependencies,
  | "api"
  | "sessionId"
  | "chatCursor"
  | "chatHasMore"
  | "chatLoadingMore"
  | "chatMessageIdsRef"
  | "ownMessageIdsRef"
  | "setChatMessages"
  | "setChatMessageIds"
  | "setChatCursor"
  | "setChatHasMore"
  | "setChatLoadingMore"
  | "tagOwnCollabsAndAdvanceRead"
  | "pushMemberActivitiesFromCollabs"
>;

type LoadOlderMessagesActionInput = Pick<
  UseSessionMainChatActionsInput,
  | "api"
  | "sessionId"
  | "chatCursor"
  | "chatHasMore"
  | "chatLoadingMore"
  | "setChatMessages"
  | "setChatMessageIds"
  | "setChatCursor"
  | "setChatHasMore"
  | "setChatLoadingMore"
  | "tagOwnCollabsAndAdvanceRead"
>;

interface OlderMessagesRequestInput {
  chatCursor: string | null;
  chatHasMore: boolean;
  chatLoadingMore: boolean;
}

interface ChatMessagesState {
  chatMap: Record<string, SessionCollab>;
  chatIds: string[];
}

interface SelectNewChatIdsInput {
  items: readonly SessionCollab[];
  existingIds: readonly string[];
}

interface SelectExternalChatIdsInput {
  newChatIds: ReadonlySet<string>;
  ownMessageIds: ReadonlySet<string>;
}

export function useSessionMainChatActions({
  api,
  sessionId,
  chatCursor,
  chatHasMore,
  chatLoadingMore,
  chatMessageIdsRef,
  ownMessageIdsRef,
  setChatMessages,
  setChatMessageIds,
  setChatCursor,
  setChatHasMore,
  setChatLoadingMore,
  tagOwnCollabsAndAdvanceRead,
  pushMemberActivitiesFromCollabs,
}: UseSessionMainChatActionsInput): SessionMainChatActions {
  const loadOlderMessages = useLoadOlderMessagesAction({
    api,
    sessionId,
    chatCursor,
    chatHasMore,
    chatLoadingMore,
    setChatMessages,
    setChatMessageIds,
    setChatCursor,
    setChatHasMore,
    setChatLoadingMore,
    tagOwnCollabsAndAdvanceRead,
  });
  const refreshChat = useRefreshChatAction({
    api,
    sessionId,
    chatMessageIdsRef,
    ownMessageIdsRef,
    setChatMessages,
    setChatMessageIds,
    setChatCursor,
    setChatHasMore,
    setChatLoadingMore,
    tagOwnCollabsAndAdvanceRead,
    pushMemberActivitiesFromCollabs,
  });

  return {
    loadOlderMessages,
    refreshChat,
  };
}

function useLoadOlderMessagesAction(
  input: LoadOlderMessagesActionInput,
): SessionMainChatActions["loadOlderMessages"] {
  const {
    api, sessionId, chatCursor, chatHasMore, chatLoadingMore, setChatMessages,
    setChatMessageIds, setChatCursor, setChatHasMore, setChatLoadingMore,
    tagOwnCollabsAndAdvanceRead,
  } = input;

  return useCallback(async (): Promise<void> => {
    const request = selectOlderMessagesRequest({
      chatCursor,
      chatHasMore,
      chatLoadingMore,
    });
    if (request.kind === OlderMessagesRequestKind.Skip) {
      return;
    }

    if (request.kind === OlderMessagesRequestKind.Exhausted) {
      setChatHasMore(false);
      return;
    }

    setChatLoadingMore(true);
    try {
      const response = await api.sessions.byId(sessionId).collabs({
        query: {
          sessionLevel: true,
          limit: CHAT_PAGE_LIMIT,
          cursor: request.cursor,
        },
      });
      const items = response.items.map((collab) => toSessionCollab({ collab }));
      applyOlderMessagesResponse({
        items,
        nextCursor: response.nextCursor,
        setChatMessages,
        setChatMessageIds,
        setChatCursor,
        setChatHasMore,
        tagOwnCollabsAndAdvanceRead,
      });
    } catch (error) {
    } finally {
      setChatLoadingMore(false);
    }
  }, [
    api, chatCursor, chatHasMore, chatLoadingMore, sessionId, setChatCursor,
    setChatHasMore, setChatLoadingMore, setChatMessageIds, setChatMessages,
    tagOwnCollabsAndAdvanceRead,
  ]);
}

function useRefreshChatAction(input: Pick<
  UseSessionMainChatActionsInput,
  | "api"
  | "sessionId"
  | "chatMessageIdsRef"
  | "ownMessageIdsRef"
  | "setChatMessages"
  | "setChatMessageIds"
  | "setChatCursor"
  | "setChatHasMore"
  | "setChatLoadingMore"
  | "tagOwnCollabsAndAdvanceRead"
  | "pushMemberActivitiesFromCollabs"
>): SessionMainChatActions["refreshChat"] {
  const {
    api, sessionId, chatMessageIdsRef, ownMessageIdsRef, setChatMessages,
    setChatMessageIds, setChatCursor, setChatHasMore, setChatLoadingMore,
    tagOwnCollabsAndAdvanceRead, pushMemberActivitiesFromCollabs,
  } = input;

  return useCallback(async (): Promise<void> => {
    try {
      const response = await api.sessions.byId(sessionId).collabs({
        query: {
          sessionLevel: true,
          limit: CHAT_PAGE_LIMIT,
        },
      });
      const items = response.items.map((collab) => toSessionCollab({ collab }));
      const { chatMap, chatIds } = selectChatMessagesState({ items });
      const newChatIds = selectNewChatIds({
        items,
        existingIds: chatMessageIdsRef.current,
      });
      applyRefreshedChatResponse({
        items,
        chatMap,
        chatIds,
        newChatIds,
        ownMessageIds: ownMessageIdsRef.current,
        nextCursor: response.nextCursor,
        setChatMessages,
        setChatMessageIds,
        setChatCursor,
        setChatHasMore,
        setChatLoadingMore,
        tagOwnCollabsAndAdvanceRead,
        pushMemberActivitiesFromCollabs,
      });
    } catch (error) {}
  }, [
    api, chatMessageIdsRef, ownMessageIdsRef, pushMemberActivitiesFromCollabs,
    sessionId, setChatCursor, setChatHasMore, setChatLoadingMore,
    setChatMessageIds, setChatMessages, tagOwnCollabsAndAdvanceRead,
  ]);
}

function applyOlderMessagesResponse({
  items,
  nextCursor,
  setChatMessages,
  setChatMessageIds,
  setChatCursor,
  setChatHasMore,
  tagOwnCollabsAndAdvanceRead,
}: Pick<
  UseSessionMainChatActionsInput,
  | "setChatMessages"
  | "setChatMessageIds"
  | "setChatCursor"
  | "setChatHasMore"
  | "tagOwnCollabsAndAdvanceRead"
> & {
  items: readonly SessionCollab[];
  nextCursor: string | null;
}): void {
  setChatMessages((prev) =>
    mergeCollabMap({
      previous: prev,
      items,
    }),
  );

  setChatMessageIds((prev) => {
    const existingIds = new Set(prev);
    const olderIds = items
      .map((message) => message.id)
      .filter((id) => !existingIds.has(id));
    return [...olderIds, ...prev];
  });

  setChatCursor(nextCursor);
  setChatHasMore(nextCursor != null);

  tagOwnCollabsAndAdvanceRead({ productId: null, collabs: items });
}

function applyRefreshedChatResponse({
  items,
  chatMap,
  chatIds,
  newChatIds,
  ownMessageIds,
  nextCursor,
  setChatMessages,
  setChatMessageIds,
  setChatCursor,
  setChatHasMore,
  setChatLoadingMore,
  tagOwnCollabsAndAdvanceRead,
  pushMemberActivitiesFromCollabs,
}: Pick<
  UseSessionMainChatActionsInput,
  | "setChatMessages"
  | "setChatMessageIds"
  | "setChatCursor"
  | "setChatHasMore"
  | "setChatLoadingMore"
  | "tagOwnCollabsAndAdvanceRead"
  | "pushMemberActivitiesFromCollabs"
> & {
  items: readonly SessionCollab[];
  chatMap: Record<string, SessionCollab>;
  chatIds: string[];
  newChatIds: ReadonlySet<string>;
  ownMessageIds: ReadonlySet<string>;
  nextCursor: string | null;
}): void {
  setChatMessages(chatMap);
  setChatMessageIds(chatIds);
  setChatCursor(nextCursor);
  setChatHasMore(nextCursor != null);
  setChatLoadingMore(false);

  tagOwnCollabsAndAdvanceRead({ productId: null, collabs: items });
  pushMemberActivitiesFromCollabs({
    collabs: items,
    arrivalIds: selectExternalChatIds({
      newChatIds,
      ownMessageIds,
    }),
  });
}

function selectOlderMessagesRequest({
  chatCursor,
  chatHasMore,
  chatLoadingMore,
}: OlderMessagesRequestInput): OlderMessagesRequest {
  if (!chatHasMore || chatLoadingMore) {
    return { kind: OlderMessagesRequestKind.Skip };
  }

  if (!chatCursor) {
    return { kind: OlderMessagesRequestKind.Exhausted };
  }

  return { kind: OlderMessagesRequestKind.Ready, cursor: chatCursor };
}

function selectChatMessagesState(input: {
  items: readonly SessionCollab[];
}): ChatMessagesState {
  const chatMap: Record<string, SessionCollab> = {};
  const chatIds: string[] = [];
  input.items.forEach((message) => {
    chatMap[message.id] = message;
    chatIds.push(message.id);
  });
  return { chatMap, chatIds };
}

function selectNewChatIds({
  items,
  existingIds,
}: SelectNewChatIdsInput): Set<string> {
  const existingChatIds = new Set(existingIds);
  return new Set(
    items
      .map((message) => message.id)
      .filter((id) => !existingChatIds.has(id)),
  );
}

function selectExternalChatIds({
  newChatIds,
  ownMessageIds,
}: SelectExternalChatIdsInput): Set<string> {
  return new Set(
    Array.from(newChatIds).filter((id) => !ownMessageIds.has(id)),
  );
}
